import React, { useState } from 'react';
import { Tag, Loader2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const BackendUrl = process.env.REACT_APP_Backend_Url;

const PromoCodeInput = ({ orderAmount, onPromoApplied, userId, products }) => {
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setMessage({ type: 'error', text: 'Veuillez entrer un code promo' });
      return;
    }

    setIsValidating(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(
        `${BackendUrl}/api/promocodes/validate`,
        {
          code: promoCode.toUpperCase().trim(),
          orderAmount,
          userId,
          products: products?.map(p => p._id),
        }
      );

      if (response.data.valid) {
        setAppliedPromo(response.data);
        setMessage({
          type: 'success',
          text: `Code appliqué ! Réduction de ${response.data.discount.toLocaleString('fr-FR')} €`,
        });

        // Sauvegarder dans localStorage
        localStorage.setItem('appliedPromoCode', JSON.stringify(response.data));

        // Appeler le callback parent
        if (onPromoApplied) {
          onPromoApplied({
            discount: response.data.discount,
            finalAmount: response.data.finalAmount,
            promoCodeId: response.data.promoCode.id,
            code: response.data.promoCode.code,
          });
        }
      }
    } catch (error) {
      console.error('Erreur validation code promo:', error);
      const errorMessage = error.response?.data?.message || 'Code promo invalide';
      setMessage({ type: 'error', text: errorMessage });
      setAppliedPromo(null);
      
      // Supprimer de localStorage
      localStorage.removeItem('appliedPromoCode');

      // Réinitialiser la réduction
      if (onPromoApplied) {
        onPromoApplied({ discount: 0, finalAmount: orderAmount });
      }
    } finally {
      setIsValidating(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode('');
    setAppliedPromo(null);
    setMessage({ type: '', text: '' });
    localStorage.removeItem('appliedPromoCode');

    if (onPromoApplied) {
      onPromoApplied({ discount: 0, finalAmount: orderAmount });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      validatePromoCode();
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center mb-3">
        <Tag className="w-5 h-5 text-orange-600 mr-2" />
        <h3 className="font-semibold text-gray-800">Code Promo</h3>
      </div>

      {!appliedPromo ? (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              onKeyPress={handleKeyPress}
              placeholder="Entrez votre code"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent uppercase"
              disabled={isValidating}
            />
            <button
              onClick={validatePromoCode}
              disabled={isValidating || !promoCode.trim()}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
            >
              {isValidating ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Appliquer'
              )}
            </button>
          </div>

          {message.text && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 flex-shrink-0" />
              )}
              <p>{message.text}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-semibold text-green-800">
                  {appliedPromo.promoCode.code}
                </p>
                <p className="text-sm text-green-600">
                  Réduction : {appliedPromo.discount.toLocaleString('fr-FR')} €
                </p>
                {appliedPromo.promoCode.description && (
                  <p className="text-xs text-green-600 mt-1">
                    {appliedPromo.promoCode.description}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={removePromoCode}
              className="text-red-600 hover:text-red-700 font-medium text-sm"
            >
              Retirer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodeInput;
